import React, { useEffect, useState } from 'react';

import { MIN_PASSWORD_LENGTH, ROUTES } from '@elara/client-core/constants';
import { formatPasswordRequirementsError } from '@elara/client-core/lib/auth-error-utils';

import { useNavigate, useQueryParams, useSessionState } from '#states/hooks.js';
import { Alert, Button, FloatingInput } from '#ui/index.js';
import { SessionLayout } from '#views/layout/SessionLayout.jsx';
import { Link, LinkButton } from '#views/Router.jsx';

export function ResetPasswordPage() {
  const sessionState = useSessionState();
  const navigate = useNavigate();
  const query = useQueryParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [verifiedEmail, setVerifiedEmail] = useState<string | undefined>();

  const oobCode = query.oobCode ?? '';
  const mode = query.mode;
  const continueUrl = query.continueUrl;

  useEffect(() => {
    if (!oobCode) return;
    setIsVerifying(true);
    sessionState
      .verifyPasswordResetCode(oobCode)
      .then((email) => setVerifiedEmail(email))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Invalid or expired reset link');
      })
      .finally(() => setIsVerifying(false));
  }, [oobCode, sessionState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);

    if (!oobCode) {
      setError('Invalid or expired reset link. Please request a new one.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      await sessionState.confirmPasswordReset(oobCode, newPassword);
      // `continueUrl` is provided by Firebase when using ActionCodeSettings.url.
      // It's an absolute URL, but our router expects paths; always route back to login.
      if (continueUrl) {
        navigate.navigate(`${ROUTES.LOGIN}?passwordReset=1`, { replace: true });
        return;
      }
      navigate.navigate(`${ROUTES.LOGIN}?passwordReset=1`, { replace: true });
    } catch (err: unknown) {
      const friendly = formatPasswordRequirementsError(err);
      setError(friendly ?? (err instanceof Error ? err.message : 'Failed to reset password'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerContent = (
    <p>
      <Link to={ROUTES.LOGIN} className="text-accent hover:underline bg-transparent border-none p-0 m-0 cursor-pointer">
        Back to sign in
      </Link>
    </p>
  );

  if (mode && mode !== 'resetPassword') {
    return (
      <SessionLayout title="Invalid link" footer={footerContent}>
        <div className="space-y-6">
          <Alert variant="destructive">This link is not a password reset link.</Alert>
          <div className="flex flex-col gap-3">
            <LinkButton to={ROUTES.LOGIN} size="lg">
              Sign in
            </LinkButton>
            <LinkButton to={ROUTES.FORGOT_PASSWORD} variant="outline" size="lg">
              Request a new reset link
            </LinkButton>
          </div>
        </div>
      </SessionLayout>
    );
  }

  if (!oobCode) {
    return (
      <SessionLayout title="Reset password" footer={footerContent}>
        <div className="space-y-6">
          <Alert variant="destructive">This reset link is missing the required code. Please request a new one.</Alert>
          <div className="flex flex-col gap-3">
            <LinkButton to={ROUTES.LOGIN} size="lg">
              Sign in
            </LinkButton>
            <LinkButton to={ROUTES.FORGOT_PASSWORD} variant="outline" size="lg">
              Request a new reset link
            </LinkButton>
          </div>
        </div>
      </SessionLayout>
    );
  }

  return (
    <SessionLayout title="Choose a new password" footer={footerContent}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <span className="whitespace-pre-line">{error}</span>
          </Alert>
        )}
        {!error && isVerifying && <p>Checking your reset link…</p>}
        <div className="space-y-1">
          <FloatingInput
            id="new-password"
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={isSubmitting || isVerifying}
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="new-password"
          />
          <p className="text-xs text-muted-foreground mt-2">At least {MIN_PASSWORD_LENGTH} characters.</p>
        </div>
        <FloatingInput
          id="confirm-password"
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isSubmitting || isVerifying}
          minLength={MIN_PASSWORD_LENGTH}
          autoComplete="new-password"
        />
        <div className="flex flex-col">
          <Button type="submit" size="lg" disabled={isSubmitting}>
            Reset password
          </Button>
        </div>
      </form>
    </SessionLayout>
  );
}
